import type { MaskedTextBoxEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import {
	Card,
	CardBody,
	CardHeader,
} from "@tql/ui-components-react/kendo/layout";
import { MaskedTextBox } from "@tql/ui-components-react/tql/inputs/beta";

import type { ShipmentAdditionalDetails } from "~/shared/schemas/shipment-management-schema.ts";
import { formatPhoneNumber } from "~/shared/utils/string-fns.ts";

import {
	useAdditionalDetailsDraft,
	useFreightDraft,
	useShipmentValidationErrors,
} from "../../hooks/use-shipment-draft.ts";
import { ADDITIONAL_DETAILS_DEFAULT } from "../../utils/defaults.ts";

type AdditionalDetailsEditProps = {
	readonly details?: ShipmentAdditionalDetails;
};

export const AdditionalDetailsEdit = ({
	details = ADDITIONAL_DETAILS_DEFAULT,
}: AdditionalDetailsEditProps) => {
	const { additionalDetails: draftDetails, updateAdditionalDetails } =
		useAdditionalDetailsDraft();
	// Fall back to the passed-in details until a draft has been seeded so the
	// inputs always start from the current shipment data.
	const current = draftDetails ?? details;
	// Required once a commodity is marked hazmat (see validate-shipment-draft.ts).
	const additionalDetailsErrors =
		useShipmentValidationErrors()?.additionalDetails;
	const { lineItems } = useFreightDraft();
	const isHazmatRequired = (lineItems ?? []).some((item) => item.hazmat);

	return (
		<div className={"card-wrapper"}>
			<Card>
				<CardHeader>
					<h3>Reference Information</h3>
				</CardHeader>
				<CardBody>
					<div className={"reference-details reference-details-edit"}>
						<div className={"reference-row"}>
							<div className={"reference-field"}>
								<div className={"section-label"}>
									Emergency Contact Name
									{isHazmatRequired ? (
										<span className={"required"}>&nbsp;*</span>
									) : null}
								</div>
								<Input
									aria-label={"Emergency Contact Name"}
									onChange={(event) =>
										updateAdditionalDetails({
											emergencyContactName: String(event.value ?? ""),
										})
									}
									valid={!additionalDetailsErrors?.emergencyContactName}
									value={current.emergencyContactName ?? ""}
								/>
								{additionalDetailsErrors?.emergencyContactName ? (
									<p className={"validation-error"}>
										{additionalDetailsErrors.emergencyContactName}
									</p>
								) : null}
							</div>
							<div className={"reference-field"}>
								<div className={"section-label"}>
									Emergency Contact Number
									{isHazmatRequired ? (
										<span className={"required"}>&nbsp;*</span>
									) : null}
								</div>
								<MaskedTextBox
									aria-label={"Emergency Contact Number"}
									mask={"(000) 000-0000"}
									onChange={(event: MaskedTextBoxEvent) =>
										updateAdditionalDetails({
											emergencyContactNumber: event.target.value,
										})
									}
									placeholder={"(___) ___-____"}
									valid={!additionalDetailsErrors?.emergencyContactNumber}
									value={formatPhoneNumber(
										current.emergencyContactNumber ?? "",
									)}
								/>
								{additionalDetailsErrors?.emergencyContactNumber ? (
									<p className={"validation-error"}>
										{additionalDetailsErrors.emergencyContactNumber}
									</p>
								) : null}
							</div>
						</div>

						<div className={"reference-row"}>
							<div className={"reference-field"}>
								<div className={"section-label"}>Customer Reference</div>
								<Input
									aria-label={"Customer Reference"}
									onChange={(event) =>
										updateAdditionalDetails({
											customerReference: String(event.value ?? ""),
										})
									}
									value={current.customerReference ?? ""}
								/>
							</div>
							<div className={"reference-field"}>
								<div className={"section-label"}>SO Number</div>
								<Input
									aria-label={"SO Number"}
									onChange={(event) =>
										updateAdditionalDetails({
											soNumber: String(event.value ?? ""),
										})
									}
									value={current.soNumber ?? ""}
								/>
							</div>
							<div className={"reference-field"}>
								<div className={"section-label"}>PO Number</div>
								<Input
									aria-label={"PO Number"}
									onChange={(event) =>
										updateAdditionalDetails({
											customerPONumber: String(event.value ?? ""),
										})
									}
									value={current.customerPONumber ?? ""}
								/>
							</div>
						</div>
					</div>
				</CardBody>
			</Card>
		</div>
	);
};
